import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronLeft, ChevronRight, ShoppingBag, Check } from "lucide-react";
import axios from "axios";
import { API, useRequestModal, useBasket, resolveImageUrl } from "../App";
import { toast } from "sonner";

const ProductDetailPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { openModal } = useRequestModal();
  const { addItem, isInBasket } = useBasket();
  const inBasket = product ? isInBasket(product.id) : false;

  const handleAddToBasket = () => {
    if (product && !inBasket) {
      addItem(product);
      toast.success("Added to basket");
    }
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${slug}`);
        setProduct(response.data);
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-24 flex items-center justify-center">
        <div className="w-8 h-8 border border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-black pt-24 flex flex-col items-center justify-center">
        <p className="text-gray-500 mb-4">Product not found</p>
        <Link to="/products" className="text-white hover:text-[#C5A059] transition-colors">
          Back to Products
        </Link>
      </div>
    );
  }

  const allImages = [product.cover_image, ...(product.gallery_images || [])].filter(Boolean).map(resolveImageUrl);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  return (
    <div className="min-h-screen bg-black" data-testid="product-detail-page">
      {/* Back Navigation */}
      <div className="fixed top-24 left-6 sm:left-12 lg:left-24 z-20">
        <Link 
          to="/products"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
          data-testid="back-to-products"
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
          Back
        </Link>
      </div>

      {/* Main Content */}
      <div className="pt-24 lg:pt-0 lg:min-h-screen lg:grid lg:grid-cols-2">
        {/* Image Gallery */}
        <motion.div 
          className="relative h-[60vh] lg:h-screen lg:sticky lg:top-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          {allImages.length > 0 ? (
            <>
              <img 
                src={allImages[currentImageIndex]} 
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
              
              {/* Image Navigation */}
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors rounded-full"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft size={20} strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors rounded-full"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight size={20} strokeWidth={1.5} />
                  </button>
                  
                  {/* Image Indicators */}
                  <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                    {allImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentImageIndex ? 'bg-white' : 'bg-white/30'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="w-full h-full bg-[#121212] flex items-center justify-center text-gray-600">
              No Image Available
            </div>
          )}
        </motion.div>

        {/* Product Info */}
        <motion.div 
          className="px-6 sm:px-12 lg:px-16 xl:px-24 py-12 lg:py-24 flex flex-col justify-center"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="max-w-lg">
            <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mb-4">
              {product.collection || 'Collection'}
            </p>
            
            <h1 
              className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight mb-6"
              data-testid="product-name"
            >
              {product.name}
            </h1>
            
            <p className="text-gray-400 text-base font-light leading-relaxed mb-10" data-testid="product-description">
              {product.description}
            </p>

            {/* Specifications */}
            <div className="space-y-4 mb-12 border-t border-white/10 pt-8">
              {product.material && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Material</span>
                  <span className="text-white">{product.material}</span>
                </div>
              )}
              {product.finish && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Finish</span>
                  <span className="text-white">{product.finish}</span>
                </div>
              )}
              {product.dimensions && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Dimensions</span>
                  <span className="text-white">{product.dimensions}</span>
                </div>
              )}
              {product.sku && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">SKU</span>
                  <span className="text-gray-600">{product.sku}</span>
                </div>
              )}
            </div>

            {/* Action Buttons - Desktop */}
            <div className="hidden lg:flex flex-col gap-3">
              <button
                onClick={() => openModal(product)}
                className="w-full flex items-center justify-center gap-3 border border-white rounded-full text-white px-8 py-4 text-sm tracking-widest uppercase font-medium hover:bg-white hover:text-black transition-colors duration-300"
                data-testid="submit-request-btn"
              >
                Submit a Request
              </button>
              <button
                onClick={handleAddToBasket}
                disabled={inBasket}
                className={`w-full flex items-center justify-center gap-3 rounded-full px-8 py-4 text-sm tracking-widest uppercase font-medium transition-colors duration-300 ${
                  inBasket
                    ? "border border-[#C5A059]/40 text-[#C5A059]/60 cursor-default"
                    : "border border-white/30 text-white/80 hover:border-[#C5A059] hover:text-[#C5A059]"
                }`}
                data-testid="add-to-basket-btn"
              >
                {inBasket ? (
                  <><Check size={16} strokeWidth={1.5} /> In Basket</>
                ) : (
                  <><ShoppingBag size={16} strokeWidth={1.5} /> Add to Basket</>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Sticky Buttons - Mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 p-4 bg-black/90 backdrop-blur-xl border-t border-white/10">
        <div className="flex gap-3">
          <button
            onClick={() => openModal(product)}
            className="flex-1 flex items-center justify-center gap-2 border border-white rounded-full text-white px-4 py-4 text-xs tracking-widest uppercase font-medium hover:bg-white hover:text-black transition-colors duration-300"
            data-testid="submit-request-btn-mobile"
          >
            Submit a Request
          </button>
          <button
            onClick={handleAddToBasket}
            disabled={inBasket}
            className={`flex items-center justify-center gap-2 rounded-full px-5 py-4 transition-colors duration-300 ${
              inBasket
                ? "border border-[#C5A059]/40 text-[#C5A059]/60"
                : "border border-white/30 text-white/80 hover:border-[#C5A059] hover:text-[#C5A059]"
            }`}
            data-testid="add-to-basket-btn-mobile"
          >
            {inBasket ? <Check size={18} strokeWidth={1.5} /> : <ShoppingBag size={18} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Spacer for mobile sticky button */}
      <div className="lg:hidden h-24" />
    </div>
  );
};

export default ProductDetailPage;
